import React from 'react';

import autoBind from 'react-autobind';
import Switch, { Case } from 'react-switch-case';
import { CirclePicker } from 'react-color';
import { If, Then, Else } from 'react-if'

import DeckGL from "deck.gl";
import {WebMercatorViewport, FlyToInterpolator} from 'deck.gl';
import {GeoJsonLayer, IconLayer} from 'deck.gl'
import Map, { Marker, NavigationControl } from "react-map-gl";
import { EditableGeoJsonLayer } from "@deck.gl-community/editable-layers";
import {ScatterplotLayer} from '@deck.gl/layers';
import {
  TranslateMode,
  ScaleMode,
  RotateMode,
  ModifyMode,
  ViewMode,
  DrawPointMode,
  DrawLineStringMode,
  DrawPolygonMode,
  DrawRectangleMode,
  DrawCircleByBoundingBoxMode,
  DrawCircleFromCenterMode,
  DrawEllipseByBoundingBoxMode,
  DrawEllipseByCenterAndTwoPointsMode,
  MeasureDistanceMode,
  MeasureAreaMode,
  MeasureAngleMode,
  CompositeMode,
  SnappableMode,
} from '@deck.gl-community/editable-layers';
import { TextLayer } from '@deck.gl/layers';
// import { Editor, EditingMode, DrawLineStringMode, DrawPolygonMode } from "react-map-gl-draw";

import { SimpleGrid, TextInput, Textarea, Select, Text, ActionIcon, Tooltip, Popover, Button, Group, Stack, Divider } from '@mantine/core';
import VerticalZoomSlider from "../VerticalZoomSlider.jsx";
import { IconPlus, IconMinus, IconHelp, IconQrcode, IconMap, IconAlertTriangle } from '@tabler/icons-react';

// import other components within the same project here
import Config from "../../Config.jsx";

import Localization from "../../Localization.jsx"
import Geocoder from '../content/Geocoder.jsx'

import { BRAND_PRIMARY } from "../../theme/brandTheme.js";
import FilePicker from "../FilePicker.jsx";
import LocaleDetailMapBar from "./LocaleDetailMapBar.jsx"
import QRCodeDialog from "./QRCodeDialog.jsx"

import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Rectangle,
} from "react-google-maps";

const MinLocaleMapSize = 1000;
const MaxLocaleMapSize = 30000;
var oneMB = 1024 * 1024;

// Every control on the Publish & Share Settings tab shares one width so the rows
// line up down the column. It is a max rather than the old fixed 200px because
// the longest English option labels were being clipped mid-word. Measured at
// 14px in Mantine's font stack, the widest option ("Allowed for users who can
// see this epack") needs 266px of text plus 46px of input chrome (12px padding
// + the 34px chevron section) = 312px, and the widest field label ("Distribute
// assessment to students") needs 221px to stay on one line. 380 clears both.
const SETTING_FIELD_STYLE = { width: '100%', maxWidth: 380 };

// Screen-space (CSS px) radius around a box corner within which a press is treated as a
// resize (scale) grab. Independent of display density, so it behaves identically on Mac
// and on Windows 4K.
const ScaleHandleGrabRadiusPx = 24;

// Ray-casting point-in-polygon test. `point` and `vertices` are [x, y] screen coords.
function screenPointInPolygon(point, vertices) {
  let inside = false;
  for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
    const xi = vertices[i][0];
    const yi = vertices[i][1];
    const xj = vertices[j][0];
    const yj = vertices[j][1];
    const intersects =
      (yi > point[1]) !== (yj > point[1]) &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

// EditableGeoJsonLayer decides scale-vs-translate, and drives the resize cursor, from
// GPU picking, and re-picks on every pan event while dragging. On high-DPI displays
// (e.g. Windows 4K) that GPU picking is both slow (each glReadPixels stalls the main
// thread, so hover picks back up and the decision lags a second or two behind the click)
// and imprecise (the small corner handles only register a dead-center hit). This subclass
// addresses both:
//
//  1. Hit-test GEOMETRICALLY instead of via GPU picking (see _geometricPicks). The frame
//     is a simple rectangle, so its corners are projected to screen and tested in CSS
//     pixels: within ScaleHandleGrabRadiusPx of a corner => resize handle; otherwise
//     inside the rectangle => move. No GPU work, so no stalls/backlog and no lag, and the
//     grab zone is precise and identical at any display density.
//
//  2. During a drag (between panstart and panend), skip hit-testing entirely. The
//     translate/scale handlers act on the edit handle and geometry captured at drag start.
//
//  3. At drag start, re-decide from a fresh geometric hit-test at the PRESS location
//     (recovered from the gesture delta), not from the lagged hover state, so an immediate
//     click near a corner still resizes instead of moving the box.
//
//  4. Reduce hover redraws (see onPointerMove / getModeProps): the base layer forces a
//     deck redraw on every hover move, which on 4K backs up the main thread and delays the
//     events that arm the handle. Redraw only when the cursor actually changes.
class DragOptimizedEditableGeoJsonLayer extends EditableGeoJsonLayer {
  getPicks(screenCoords) {
    if (this._dragStarting) {
      // Inside the base _onpanstart; skip its pick (we either re-picked just before, or
      // are trusting the handle hover already armed).
      return [];
    }
    const editState = this.state && this.state._editableLayerState;
    if (editState && editState.pointerDownPicks !== null && editState.pointerDownPicks !== undefined) {
      // A drag is in progress; the drag handlers don't use live picks.
      return [];
    }
    return this._geometricPicks(screenCoords);
  }

  // Fully geometric hit-test for the field trip area, replacing deck.gl GPU picking.
  // GPU picking on high-DPI displays (Windows 4K) is both slow -- each glReadPixels
  // stalls the main thread, so hover picks back up and the scale/translate decision lags
  // a second or two behind the click -- and imprecise (corner handles only register dead
  // center). The frame is a simple rectangle, so we instead project its corners to screen
  // and test in CSS pixels: within ScaleHandleGrabRadiusPx of a corner => resize handle;
  // otherwise inside the rectangle => move. This is cheap (no GPU, no stalls, no backlog),
  // precise, and behaves identically at any display density. Falls back to GPU picking if
  // the geometry/viewport isn't available yet.
  _geometricPicks(screenCoords) {
    try {
      const viewport = this.context && this.context.viewport;
      const mode = this.getActiveMode();
      const modes = (mode && mode._modes) || (mode ? [mode] : []);
      let cornerHandles = null;
      for (const m of modes) {
        if (m && Array.isArray(m._cornerGuidePoints) && m._cornerGuidePoints.length >= 3) {
          cornerHandles = m._cornerGuidePoints;
          break;
        }
      }
      if (!viewport || !cornerHandles) {
        return super.getPicks(screenCoords) || [];
      }

      const cornersScreen = [];
      for (const handle of cornerHandles) {
        const coords = handle && handle.geometry && handle.geometry.coordinates;
        if (!coords) {
          return super.getPicks(screenCoords) || [];
        }
        const projected = viewport.project([coords[0], coords[1]]);
        cornersScreen.push([projected[0], projected[1]]);
      }

      const result = [];

      // Resize: the nearest corner handle within the grab radius.
      let nearestIdx = -1;
      let nearestDist = Infinity;
      for (let i = 0; i < cornersScreen.length; i++) {
        const dx = cornersScreen[i][0] - screenCoords[0];
        const dy = cornersScreen[i][1] - screenCoords[1];
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestIdx = i;
        }
      }
      if (nearestIdx >= 0 && nearestDist <= ScaleHandleGrabRadiusPx) {
        const handle = cornerHandles[nearestIdx];
        const positionIndexes = handle.properties && handle.properties.positionIndexes;
        result.push({ isGuide: true, object: handle, index: (positionIndexes && positionIndexes[0]) || 0 });
      }

      // Move: pointer inside the box rectangle (selected feature index 0).
      const polygon = this.props.data && this.props.data.features && this.props.data.features[0];
      if (polygon && screenPointInPolygon(screenCoords, cornersScreen)) {
        result.push({ isGuide: false, index: 0, object: polygon });
      }

      return result;
    } catch (e) {
      return super.getPicks(screenCoords) || [];
    }
  }

  // The base layer fires onPointerMove on every hover move and does setState
  // ({lastPointerMoveEvent}), forcing a deck redraw each time. On 4K those redraws back up
  // the main thread, which delays the very events that arm the scale handle (hence the
  // "must wait a second before a click resizes"). Translate/Scale don't use
  // lastPointerMoveEvent, so arm the mode directly and skip that setState. The cursor is
  // still updated, but de-duplicated (see getModeProps), so a redraw happens only when the
  // cursor actually changes -- not on every move.
  onPointerMove(event) {
    const editState = this.state && this.state._editableLayerState;
    if (editState && editState.pointerDownPicks !== null && editState.pointerDownPicks !== undefined) {
      // A drag is in progress. Ignore hover moves: on some setups (Windows 4K) mjolnir
      // still emits 'pointermove' mid-drag, and because picking is skipped during a drag
      // (getPicks returns []), handlePointerMove would re-evaluate with empty picks and
      // reset _isTranslatable to false. TranslateMode.handleDragging bails out when
      // _isTranslatable is false, so the live 'translating' edits stop firing and the
      // frame only jumps to the final position on release instead of following the cursor.
      // The drag arming is fixed at drag start; it must not change mid-drag.
      console.log('[FTA] pointermove during drag - skipped (was clearing _isTranslatable)');
      return;
    }
    this.getActiveMode().handlePointerMove(event, this.getModeProps(this.props));
  }

  // De-duplicate cursor updates so an unchanged cursor doesn't trigger a redraw per move.
  getModeProps(props) {
    const modeProps = super.getModeProps(props);
    const baseOnUpdateCursor = modeProps.onUpdateCursor;
    modeProps.onUpdateCursor = (cursor) => {
      if (cursor !== this.state.cursor) {
        baseOnUpdateCursor(cursor);
      }
    };
    return modeProps;
  }

  _onpanstart(event) {
    // Decide scale-vs-translate from a fresh geometric hit-test at the PRESS location --
    // not from hover state (which lags on 4K, so an immediate click outruns it and the box
    // moves instead of resizing) and not from the panstart location (which has already
    // nudged a few px past the press point and can land outside a corner's grab zone). The
    // press point is recovered by subtracting the gesture delta, so the decision is correct
    // regardless of click speed or hover lag.
    try {
      const srcEvent = event.srcEvent;
      const current = this.getScreenCoords(srcEvent);
      const dx = typeof event.deltaX === 'number' ? event.deltaX : 0;
      const dy = typeof event.deltaY === 'number' ? event.deltaY : 0;
      const pressCoords = [current[0] - dx, current[1] - dy];
      this.onPointerMove({
        screenCoords: pressCoords,
        mapCoords: this.getMapCoords(pressCoords),
        picks: this._geometricPicks(pressCoords),
        pointerDownPicks: null,
        pointerDownScreenCoords: null,
        pointerDownMapCoords: null,
        sourceEvent: srcEvent,
        cancelPan: () => {}
      });
    } catch (e) {
      // Fall through to the base behaviour (uses the last hover arming).
    }

    // Skip the base handler's own pick (we just armed above). The in-progress drag is
    // tracked via pointerDownPicks for subsequent pan events.
    this._dragStarting = true;
    try {
      super._onpanstart(event);
    } finally {
      this._dragStarting = false;
    }
  }
}
DragOptimizedEditableGeoJsonLayer.layerName = 'DragOptimizedEditableGeoJsonLayer';

class LocaleMapStepErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Locale map step render error:", error);
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

class LocaleDetailView extends React.Component {
  MinLocaleMapSize() {
    return MinLocaleMapSize;
  }
  MaxLocaleMapSize() {
    return MaxLocaleMapSize;
  }

  constructor(props) {
    super(props);
    this.mapRef = React.createRef();

    this.updateMapZoom = this.updateMapZoom.bind(this);
    this.onEdit = this.onEdit.bind(this);
    // Handlers handed to JSX as bare `this.x` references (here and in the
    // EditLocaleSettingsPanel subclass, which renders this view through
    // super.render()). React invokes them with no receiver, so an unbound one
    // throws "undefined is not an object" on the first keystroke/click. Binding
    // in the BASE constructor is what the subclass needs too: `this` is already
    // the most-derived instance, so an override like the panel's onUploadSuccess
    // is the one that gets bound.
    this.localeNameChanged = this.localeNameChanged.bind(this);
    this.localeDescChanged = this.localeDescChanged.bind(this);
    this.revealCodeChanged = this.revealCodeChanged.bind(this);
    this.handleColorPickerClick = this.handleColorPickerClick.bind(this);
    this.handleMapRenderError = this.handleMapRenderError.bind(this);
    this.onUploadSuccess = this.onUploadSuccess.bind(this);
    this.onAbortUpload = this.onAbortUpload.bind(this);
    this.editModes = new CompositeMode([new TranslateMode(), new ScaleMode()]);
    this.pendingEditFrame = null;
    this.pendingBoxData = null;
    this.frameFromVisibleMapTimer = null;
    this._isMounted = false;
    this.mapContainerRef = React.createRef();
    this.mapResizeObserver = null;
    this.isBoxEditing = false;
    // Handle to the DeckGL instance, captured via a bound callback ref so that during a
    // box drag we can update just the box layer on the existing deck instance instead of
    // re-rendering the whole map panel each frame (see onEdit).
    this.deckGLRef = null;
    this.handleDeckRef = (handle) => {
      this.deckGLRef = handle || null;
      if (handle) {
        this.onRectangleMounted();
      }
    };

    // state variables here
    this.state = {
      isDirty: false,
      activeView: 0,
      localeName: "",
      localeDesc: "",
      localeKLA: -1,
      localeSubject: -1,
      localeCoverFileID: "",
      localeColor: BRAND_PRIMARY.substring(1),
      localeShareable: false,
      localeLanguage: "tc",
      localePublish: false,
      localeDuplicationAllowed: 0,
      hsRevealCode: "",
      hsRevealCodeEnabled: false,
      autoShareAssessment: 0,
      manualAiAssessment: 1, // epack.is_manual_ai_assessment: 1 = Manually (DB default), 0 = Automatically
      // Avatar interaction (game mode). All default OFF, matching the DB
      // defaults, so an existing locale keeps today's progress-only tap.
      moodEnabled: false,
      nudgeEnabled: false,
      chatTeacherToStudent: false,
      chatStudentToStudent: false,
      chatStudentToTeacher: false,
      colorPickerAnchor: null,
      qrDialogOpen: false,
      map: {
        center: {
          lat: 0,
          lon: 0
        },
        bounds:{
          ul:{
            lat: 0,
            lon: 0
          },
          br:{
            lat: 0,
            lon: 0
          }
        },
        screenCoordinates:{
          ul:{
            x: 0,
            y: 0
          },
          br:{
            x: 0,
            y: 0
          }
        },
        style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
        _style: "cartoon",
        lang: "zh"
      },
      localeCenter: {
        lat: 22.4191,
        lng: 114.2059,
      },
      localeUpperLeft: {
        lat: 22.4299,
        lng: 114.1942,
      },
      localeBottomRight: {
        lat: 22.4083,
        lng: 114.2176,
      },
      boxData: {
        type: "FeatureCollection",
        features:[{
          type: "Feature",
          color: BRAND_PRIMARY,
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [114.2176, 22.4083],
                [114.2176, 22.4299],
                [114.1942, 22.4299],
                [114.1942, 22.4083],
                [114.2176, 22.4083]
              ]
            ]
          }
        }]
      },
      localeRect: {
        bounds:{
          ul:{
            lat: 22.4299,
            lon: 114.1942,
          },
          br:{
            lat: 22.4083,
            lon: 114.2176,
          },
          center:{
            lat: 22.4191,
            lon: 114.2059
          }
        }
      },
      viewState:{
        latitude: 22.4191,
        longitude: 114.2059,
        zoom: 15,
        bearing: 0,
        pitch: 0,
        width: 500,
        height: 500
      },
      zoom: 15,
      localeBoundaryRect: null,
      localeCoverFilePath: "",
      localeCoverPreviewPath: "",
      // The raw file.path / file.tn_path as they sit on the epack row, kept
      // next to the display paths above (which carry the UPLOAD_PATH prefix).
      // EditLocaleDialog writes these back into the cached locale after a save
      // so a replaced cover does not need a re-fetch to be seen.
      localeCoverRawPath: "",
      localeCoverRawPreviewPath: "",
      modifyModeContainer: new ModifyMode(),
      flyContainer: new FlyToInterpolator({speed: 2}),
      isTeleportButtonDisabled: false,
      webGLSupported: true,
      mapRenderError: null,
    };
    autoBind(this);

    let currentLocale = this.props.root.getCurrentLocale();
    if (currentLocale) {
      console.log("current locale:", currentLocale);
      this.state.localeName = currentLocale.name;
      this.state.localeDesc = currentLocale.description;
      this.state.localeKLA = parseInt(currentLocale.kla);
      this.state.localeSubject = parseInt(currentLocale.subject);
      this.state.localeCoverFileID = currentLocale.tn_file_id;
      this.state.localeColor = parseInt(currentLocale.color).toString(16).padStart(6, '0');
      this.state.localeShareable = parseInt(currentLocale.is_share) > 0;
      this.state.localeLanguage = currentLocale.lang;
      this.state.localePublish = parseInt(currentLocale.is_ready) > 0;
      this.state.localeDuplicationAllowed = parseInt(currentLocale.is_duplication_allowed) || 0;
      this.state.hsRevealCode = currentLocale.hs_reveal_code;
      this.state.hsRevealCodeEnabled = parseInt(currentLocale.hs_reveal_code_enabled) > 0;
      this.state.autoShareAssessment = parseInt(currentLocale.is_auto_share_assessment) || 0;
      // NaN-safe: 0 (Automatically) must be preserved, so no `|| 1`; default 1 (Manually)
      // when the column is absent from an older cached row.
      const manualAi = parseInt(currentLocale.is_manual_ai_assessment, 10);
      this.state.manualAiAssessment = Number.isNaN(manualAi) ? 1 : manualAi;
      // Avatar interaction. `parseInt(...) > 0` is NaN-safe (NaN > 0 is false),
      // so a locale row cached before the columns existed reads as OFF rather
      // than throwing — same shape as hsRevealCodeEnabled above.
      this.state.moodEnabled = parseInt(currentLocale.is_mood_enabled) > 0;
      this.state.nudgeEnabled = parseInt(currentLocale.is_nudge_enabled) > 0;
      this.state.chatTeacherToStudent = parseInt(currentLocale.is_chat_teacher_to_student) > 0;
      this.state.chatStudentToStudent = parseInt(currentLocale.is_chat_student_to_student) > 0;
      this.state.chatStudentToTeacher = parseInt(currentLocale.is_chat_student_to_teacher) > 0;
      this.state.localeCenter = {lat: parseFloat(currentLocale.mid_lat), lng: parseFloat(currentLocale.mid_lon)};
      this.state.localeUpperLeft = {lat: parseFloat(currentLocale.ul_lat), lng: parseFloat(currentLocale.ul_lon)};
      this.state.localeBottomRight = {lat: parseFloat(currentLocale.br_lat), lng: parseFloat(currentLocale.br_lon)};
      // CHANGE before deployment
      this.state.localeCoverFilePath = Config.UPLOAD_PATH() + currentLocale.path;
      this.state.localeCoverPreviewPath = Config.UPLOAD_PATH() + currentLocale.tn_path;
      this.state.localeCoverRawPath = currentLocale.path || "";
      this.state.localeCoverRawPreviewPath = currentLocale.tn_path || "";
      this.state.viewState = {
        latitude: parseFloat(currentLocale.mid_lat),
        longitude: parseFloat(currentLocale.mid_lon),
        zoom: 15,
        bearing: 0,
        pitch: 0,
        width: 500,
        height: 500,
        maxZoom: 17,
        minZoom: 3
      };
      this.state.map = {
        zoom: 0,
        center: {
          lat: this.state.localeCenter.lat,
          lon: this.state.localeCenter.lng
        },
        bounds:{
          ul:{
            lat: this.state.localeUpperLeft.lat,
            lon: this.state.localeUpperLeft.lng
          },
          br:{
            lat: this.state.localeBottomRight.lat,
            lon: this.state.localeBottomRight.lng
          }
        },
        screenCoordinates:{
          ul:{
            x: 0,
            y: 0
          },
          br:{
            x: 0,
            y: 0
          }
        },
        style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
        _style: "cartoon",
        lang: "zh"
      };
      this.state.boxData = {
        type: "FeatureCollection",
        features:[{
          type: "Feature",
          color: this.state.localeColor,
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [this.state.localeBottomRight.lng, this.state.localeBottomRight.lat],
                [this.state.localeBottomRight.lng, this.state.localeUpperLeft.lat],
                [this.state.localeUpperLeft.lng, this.state.localeUpperLeft.lat],
                [this.state.localeUpperLeft.lng, this.state.localeBottomRight.lat],
                [this.state.localeBottomRight.lng, this.state.localeBottomRight.lat]
              ]
            ]
          }
        }]
      };
      this.state.localeRect = {
        bounds:{
          ul:{
            lat: this.state.localeUpperLeft.lat,
            lon: this.state.localeUpperLeft.lng
          },
          br:{
            lat: this.state.localeBottomRight.lat,
            lon: this.state.localeBottomRight.lng
          },
          center:{
            lat: this.state.localeCenter.lat,
            lon: this.state.localeCenter.lng
          }
        }
      };
    }

    if (this.props.activeView) {
      this.state.activeView = this.props.activeView;
    }

    if (this.props.parent.state.title != null) {
      this.props.parent.setState({ title: this.state.localeName });
    }
  }

  componentDidMount() {
    // Remove duplicate API call - parent dialog handles duplication check
    this._isMounted = true;
    this.checkWebGLSupport();
    this.updateTeleportButtonDisabledState();
    this.syncMapLanguageStyle();
    if (this.state.activeView === 1) {
      this.observeMapContainer();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.unobserveMapContainer();
    if (this.pendingEditFrame) {
      window.cancelAnimationFrame(this.pendingEditFrame);
      this.pendingEditFrame = null;
    }
    if (this.frameFromVisibleMapTimer) {
      clearTimeout(this.frameFromVisibleMapTimer);
      this.frameFromVisibleMapTimer = null;
    }
    this.pendingBoxData = null;
  }

  // Keep the map style in sync with the current UI language. Runs on mount/update instead
  // of in render(): mutating state and calling setState during render is a React
  // anti-pattern that forces React to discard and redo the in-progress render.
  syncMapLanguageStyle() {
    const lang = Localization.strings.getLanguage();
    if (lang === this.state.map.lang) {
      return;
    }
    const style = Config.MBOX_STYLE(lang)[this.state.map._style];
    if (!style) {
      // Unknown style; just record the language so we don't re-run every update.
      this.state.map.lang = lang;
      return;
    }
    this.state.map.lang = lang;
    this.state.map.style = style;
    this.setState({
      map: {
        ...this.state.map,
        lang,
        style,
      }
    });
  }

  getDevicePixels() {
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    // Cap GPU resolution on high-DPI displays (common on Windows 4K at 150–200% scaling).
    return Math.min(dpr, 1.5);
  }

  updateMapScreenCoordinates() {
    const el = this.mapContainerRef.current;
    if (!el) {
      return;
    }
    const { width, height } = el.getBoundingClientRect();
    this.state.map.screenCoordinates.ul.x = 0;
    this.state.map.screenCoordinates.ul.y = 0;
    this.state.map.screenCoordinates.br.x = width;
    this.state.map.screenCoordinates.br.y = height;
  }

  getMapViewportSize() {
    const el = this.mapContainerRef.current;
    if (el) {
      const { width, height } = el.getBoundingClientRect();
      if (width > 0 && height > 0) {
        return { width, height };
      }
    }

    const screenCoordinates = this.state.map?.screenCoordinates;
    const width = screenCoordinates?.br?.x || this.state.viewState.width;
    const height = screenCoordinates?.br?.y || this.state.viewState.height;
    if (width > 0 && height > 0) {
      return { width, height };
    }

    return null;
  }

  getVisibleMapBounds(viewState = this.state.viewState) {
    const size = this.getMapViewportSize();
    if (!size) {
      return null;
    }

    const viewport = new WebMercatorViewport({
      longitude: viewState.longitude,
      latitude: viewState.latitude,
      zoom: viewState.zoom,
      bearing: viewState.bearing || 0,
      pitch: viewState.pitch || 0,
      width: size.width,
      height: size.height
    });
    const ul = viewport.unproject([0, 0]);
    const br = viewport.unproject([size.width, size.height], false);

    return {
      ul: { lon: ul[0], lat: ul[1] },
      br: { lon: br[0], lat: br[1] }
    };
  }

  hasUsableBounds(bounds) {
    return Boolean(
      bounds &&
      Number.isFinite(bounds.ul?.lat) &&
      Number.isFinite(bounds.ul?.lon) &&
      Number.isFinite(bounds.br?.lat) &&
      Number.isFinite(bounds.br?.lon) &&
      Math.abs(bounds.ul.lat - bounds.br.lat) > 0.000001 &&
      Math.abs(bounds.ul.lon - bounds.br.lon) > 0.000001
    );
  }

  syncVisibleMapBounds(viewState = this.state.viewState) {
    const bounds = this.getVisibleMapBounds(viewState);
    if (!this.hasUsableBounds(bounds)) {
      return null;
    }

    this.state.map.zoom = viewState.zoom;
    this.state.map.center.lon = viewState.longitude;
    this.state.map.center.lat = viewState.latitude;
    this.state.map.bounds = bounds;
    return bounds;
  }

  observeMapContainer() {
    const el = this.mapContainerRef.current;
    if (!el || this.mapResizeObserver) {
      return;
    }
    this.updateMapScreenCoordinates();
    if (typeof ResizeObserver !== 'undefined') {
      this.mapResizeObserver = new ResizeObserver(() => {
        this.updateMapScreenCoordinates();
      });
      this.mapResizeObserver.observe(el);
    }
  }

  unobserveMapContainer() {
    if (this.mapResizeObserver) {
      this.mapResizeObserver.disconnect();
      this.mapResizeObserver = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Update shareable state when duplication status changes
    if (this.props.isDuplicateCheckComplete && 
        prevProps.isDuplicateCheckComplete !== this.props.isDuplicateCheckComplete &&
        this.props.isDuplicate) {
      // If this is a duplicate, disable sharing
      this.setState({ localeShareable: false });
      this.state.localeShareable = false;
    }

    if (prevState.activeView !== this.state.activeView) {
      if (this.state.activeView === 1) {
        this.observeMapContainer();
      } else {
        this.unobserveMapContainer();
      }
    }

    // Sync map style to the UI language here (not in render) to avoid setState-during-render.
    this.syncMapLanguageStyle();
  }

  updateTeleportButtonDisabledState() {
    const { map, localeRect } = this.state;
    if (!map || !map.bounds || !localeRect || !localeRect.bounds) {
      return;
    }

    const mapBounds = map.bounds;
    const localeBounds = localeRect.bounds;

    const isVisible =
      localeBounds.ul.lat <= mapBounds.ul.lat &&
      localeBounds.br.lat >= mapBounds.br.lat &&
      localeBounds.ul.lon >= mapBounds.ul.lon &&
      localeBounds.br.lon <= mapBounds.br.lon;

    if (this.state.isTeleportButtonDisabled !== isVisible) {
      this.setState({ isTeleportButtonDisabled: isVisible });
    }
  }

  checkWebGLSupport() {
    try {
      const canvas = document.createElement('canvas');
      // deck.gl 9 requires WebGL2, so probing WebGL1 would pass in browsers
      // where only WebGL2 is blocked and the map would crash past the fallback.
      const gl = canvas.getContext('webgl2');
      // Release the probe context right away: browsers cap live WebGL contexts
      // (~16 in Chrome), and a leaked probe per mount can starve the real maps.
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      this.setState({ webGLSupported: Boolean(gl) });
    } catch (e) {
      console.error('Locale map WebGL detection error:', e);
      this.setState({ webGLSupported: false, mapRenderError: e });
    }
  }

  handleMapRenderError(error) {
    console.error('Locale map render error:', error);
    this.setState({ webGLSupported: false, mapRenderError: error });
  }

  renderMapFallback() {
    return (
      <div
        style={{
          height: '57vh',
          minHeight: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          padding: 24,
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 4,
          color: '#555',
        }}
      >
        <IconMap size={48} style={{ color: '#9e9e9e', marginBottom: 12 }} />
        <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
          {Localization.strings.MAP_LOAD_FAILED || 'Map failed to load'}
        </div>
        <div style={{ maxWidth: 520 }}>
          {Localization.strings.MAP_LOAD_FAILED_REMINDER || 'Please reload the page or try another browser/device. If the problem persists, contact support.'}
        </div>
      </div>
    );
  }

  zoomIn = () => {
    let newZoom = Math.round(this.state.viewState.zoom) + 1;
    if (newZoom > this.state.viewState.maxZoom) {
      newZoom = this.state.viewState.maxZoom;
    }
    this.updateMapZoom(newZoom);
  }

  zoomOut = () => {
    let newZoom = Math.round(this.state.viewState.zoom) - 1;
    if (newZoom < this.state.viewState.minZoom) {
      newZoom = this.state.viewState.minZoom;
    }
    this.updateMapZoom(newZoom);
  }

  changeActiveView(index) {
    this.setState({ activeView: index });
    this.state.activeView = index;
    
    // In creation mode, automatically set the frame to match visible map area when entering the "Define Field Trip Area" tab
    if (index === 1 && !this.props.root.getCurrentLocale() && this.state.webGLSupported) {
      // Use setTimeout to ensure the map is rendered and bounds are calculated.
      // Store the timer id so it can be cleared on unmount (avoids setState after unmount).
      if (this.frameFromVisibleMapTimer) {
        clearTimeout(this.frameFromVisibleMapTimer);
      }
      this.frameFromVisibleMapTimer = setTimeout(() => {
        this.frameFromVisibleMapTimer = null;
        this.setFrameFromVisibleMap();
      }, 300);
    }
  }

  resetToDefaults() {
    // Reset all state values to their initial defaults
    const defaultState = {
      isDirty: false,
      activeView: 0,
      localeName: "",
      localeDesc: "",
      localeKLA: -1,
      localeSubject: -1,
      localeCoverFileID: "",
      localeColor: BRAND_PRIMARY.substring(1),
      localeShareable: false,
      localeLanguage: "tc",
      localePublish: false,
      localeDuplicationAllowed: 0,
      hsRevealCode: "",
      hsRevealCodeEnabled: false,
      autoShareAssessment: 0,
      manualAiAssessment: 1,
      moodEnabled: false,
      nudgeEnabled: false,
      chatTeacherToStudent: false,
      chatStudentToStudent: false,
      chatStudentToTeacher: false,
      colorPickerAnchor: null,
      localeCenter: {
        lat: 22.4191,
        lng: 114.2059,
      },
      localeUpperLeft: {
        lat: 22.4299,
        lng: 114.1942,
      },
      localeBottomRight: {
        lat: 22.4083,
        lng: 114.2176,
      },
      localeCoverFilePath: "",
      localeCoverPreviewPath: "",
      localeCoverRawPath: "",
      localeCoverRawPreviewPath: "",
      isTeleportButtonDisabled: false
    };

    // Reset boxData and localeRect
    defaultState.boxData = {
      type: "FeatureCollection",
      features:[{
        type: "Feature",
        color: defaultState.localeColor,
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [defaultState.localeBottomRight.lng, defaultState.localeBottomRight.lat],
              [defaultState.localeBottomRight.lng, defaultState.localeUpperLeft.lat],
              [defaultState.localeUpperLeft.lng, defaultState.localeUpperLeft.lat],
              [defaultState.localeUpperLeft.lng, defaultState.localeBottomRight.lat],
              [defaultState.localeBottomRight.lng, defaultState.localeBottomRight.lat]
            ]
          ]
        }
      }]
    };

    defaultState.localeRect = {
      bounds:{
        ul:{
          lat: defaultState.localeUpperLeft.lat,
          lon: defaultState.localeUpperLeft.lng
        },
        br:{
          lat: defaultState.localeBottomRight.lat,
          lon: defaultState.localeBottomRight.lng
        },
        center:{
          lat: defaultState.localeCenter.lat,
          lon: defaultState.localeCenter.lng
        }
      }
    };

    defaultState.viewState = {
      latitude: defaultState.localeCenter.lat,
      longitude: defaultState.localeCenter.lng,
      zoom: 15,
      bearing: 0,
      pitch: 0,
      width: 500,
      height: 500,
      maxZoom: 17,
      minZoom: 3
    };

    defaultState.map = {
      center: {
        lat: defaultState.localeCenter.lat,
        lon: defaultState.localeCenter.lng
      },
      bounds:{
        ul:{
          lat: defaultState.localeUpperLeft.lat,
          lon: defaultState.localeUpperLeft.lng
        },
        br:{
          lat: defaultState.localeBottomRight.lat,
          lon: defaultState.localeBottomRight.lng
        }
      },
      screenCoordinates:{
        ul:{
          x: 0,
          y: 0
        },
        br:{
          x: 0,
          y: 0
        }
      },
      style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
      _style: "cartoon",
      lang: "zh"
    };

    // Update both state and this.state
    this.setState(defaultState);
    Object.assign(this.state, defaultState);
  }

  checkDirty(oldValue, newValue) {
    let dirty = (oldValue != newValue);
    this.setState({ isDirty: dirty });
    this.state.isDirty = dirty;
  }

  localeNameChanged(e) {
    this.setState({
      localeName: e.target.value
    });
    this.state.localeName = e.target.value;

    if (this.props.root.getCurrentLocale()) {
      this.checkDirty(this.props.root.getCurrentLocale().name, e.target.value);
    }

    if (this.props.parent.state.title != null) {
      this.props.parent.setState({ title: this.state.localeName });
    }
  }

  localeDescChanged(e) {
    this.setState({
      localeDesc: e.target.value
    })
    this.state.localeDesc = e.target.value;

    if (this.props.root.getCurrentLocale()) {
      this.checkDirty(this.props.root.getCurrentLocale().description, e.target.value);
    }
  }

  localeKLAChanged(e) {
    this.setState({localeKLA: e.target.value});
    this.state.localeKLA = e.target.value;

    this.setState({ localeSubject: 0 });
    this.state.localeSubject = 0;

    if (this.props.root.getCurrentLocale()) {
      this.checkDirty(this.props.root.getCurrentLocale().kla, e.target.value);
    }
  }

  localeSubjectChanged(e) {
    this.setState({localeSubject: e.target.value});
    this.state.localeSubject = e.target.value;

    if (this.props.root.getCurrentLocale()) {
      this.checkDirty(this.props.root.getCurrentLocale().subject, e.target.value);
    }
  }

  localeColorChanged(color, event) {
    this.updateMapState();

    this.setState({localeColor: color.hex.substring(1)});
    this.state.localeColor = color.hex.substring(1);
  }

  handleColorPickerClick(event) {
    // Toggle so re-clicking the swatch closes the popover as well as opens it.
    const target = event.currentTarget;
    this.setState((prev) => ({
      colorPickerAnchor: prev.colorPickerAnchor ? null : target,
    }));
  }

  handleColorPickerClose() {
    this.setState({
      colorPickerAnchor: null,
    });
  }

  localeLanguageChanged(e) {
    this.setState({localeLanguage: e.target.value});
    this.state.localeLanguage = e.target.value;
  }

  localeShareableChanged(e) {
    this.setState({localeShareable: e.target.value});
    this.state.localeShareable = e.target.value;
  }

  localePublishChanged(e) {
    this.setState({localePublish: e.target.value});
    this.state.localePublish = e.target.value;
  }

  localeDuplicationChanged(e) {
    this.setState({localeDuplicationAllowed: e.target.value});
    this.state.localeDuplicationAllowed = e.target.value;
  }

  enableRevealCodeChanged(e) {
    this.setState({hsRevealCodeEnabled: e.target.value});
    this.state.hsRevealCodeEnabled = e.target.value;
  }

  revealCodeChanged(e) {
    this.setState({
      hsRevealCode: e.target.value
    })
    this.state.hsRevealCode = e.target.value;

    if (this.props.root.getCurrentLocale()) {
      this.checkDirty(this.props.root.getCurrentLocale().hs_reveal_code, e.target.value);
    }
  }

  autoShareAssessmentChanged(e) {
    const value = parseInt(e.target.value, 10);
    const intValue = (Number.isNaN(value) || value < 0 || value > 3) ? 0 : value;
    this.setState({autoShareAssessment: intValue});
    this.state.autoShareAssessment = intValue;
  }

  manualAiAssessmentChanged(e) {
    const value = parseInt(e.target.value, 10);
    // Clamp to 0 (Automatically) / 1 (Manually); anything else falls back to the default 1.
    const intValue = (value === 0 || value === 1) ? value : 1;
    this.setState({manualAiAssessment: intValue});
    this.state.manualAiAssessment = intValue;
  }

  /// Avatar interaction toggles. One handler for all five: the Select hands
  /// back a real boolean (see the render below), and every flag has the same
  /// shape, so five near-identical methods would only be five places to drift.
  /// Mirrors the state write into `this.state` the way every other handler in
  /// this class does — the dialogs read `refs.localeDetailView.state` directly
  /// on save, before React has flushed the setState.
  avatarInteractionChanged(field, value) {
    const boolValue = (value === true || value === "true");
    this.setState({[field]: boolValue});
    this.state[field] = boolValue;
  }

  /// App-version requirements stay available without adding persistent warning
  /// copy to the form. The yellow icon sits beside the field/section title and
  /// reveals the requirement on hover.
  renderAppVersionWarningLabel(label, message, dimmed = false) {
    return (
      <Group gap={6} align="center" wrap="nowrap">
        <Text size="sm" fw={dimmed ? 600 : 500} c={dimmed ? "dimmed" : undefined}>
          {label}
        </Text>
        <Tooltip label={message} withArrow multiline w={320}>
          <span style={{ display: 'inline-flex', cursor: 'help' }}>
            <IconAlertTriangle
              size={17}
              style={{ color: 'var(--mantine-color-yellow-6)', flexShrink: 0 }}
            />
          </span>
        </Tooltip>
      </Group>
    );
  }

  validateRevealCode() {
    let result = true;
    let code = this.state.hsRevealCode.trim();
    let illegalChars = /[\W_]/; // Regex to check for non-alphanumeric characters
    let errorMessage = "";

    // Check if the reveal code is empty
    if (code === "") {
        result = false;
        errorMessage = Localization.strings.REVEAL_CODE_REQUIREMENT_3; // "Please enter a reveal code; it cannot be empty."
    }
    // Check for illegal characters
    else if (illegalChars.test(code)) {
        result = false;
        errorMessage = Localization.strings.REVEAL_CODE_REQUIREMENT_0; // "The reveal code must contain only letters and numbers."
    }
    // Check for at least one letter and one number
    else if (code.search(/[a-zA-Z]+/) === -1 || code.search(/[0-9]+/) === -1) {
        result = false;
        errorMessage = Localization.strings.REVEAL_CODE_REQUIREMENT_1; // "The reveal code must include at least one letter and one number."
    }
    // Check for length
    else if (code.length !== 6) {
        result = false;
        errorMessage = Localization.strings.REVEAL_CODE_REQUIREMENT_2; // "The reveal code must be exactly 6 characters long."
    }

    return { isValid: result, message: errorMessage };
  }

  validateInput() {
    let isValid = true;
    let errorMessage = "";
    switch (this.state.activeView) {
      case 0:
        let name = this.state.localeName.trim();
        this.setState({ localeName: name });
        this.state.localeName = name;
        if (name == "") {
          isValid = false;
          errorMessage = Localization.strings.LOCALE_NAME_BLANK_REMINDER;
        }
        else if (this.state.localeKLA < 0) {
          isValid = false;
          errorMessage = Localization.strings.LOCALE_KLA_BLANK_REMINDER;
        }
        else if (this.state.localeSubject < 0) {
          isValid = false;
          errorMessage = Localization.strings.LOCALE_SUBJECT_BLANK_REMINDER;
        }
        if (this.state.localeCoverFileID == "") {
          isValid = false;
          errorMessage = Localization.strings.LOCALE_COVER_BLANK_REMINDER;
        }
        break;
      case 1:
        let result = this.checkLocaleBoundary();
        isValid = (result == 0);
        if (isValid) {
          this.updateMapState();
        }
        if (result < 0) {
          errorMessage = Localization.strings.LOCALE_MAP_MIN_SIZE_REMINDER;
        }
        else if (result > 0) {
          errorMessage = Localization.strings.LOCALE_MAP_MAX_SIZE_REMINDER;
        }
        break;
      case 2:
        // validate reveal code if reveal code is enabled
        if (this.state.hsRevealCodeEnabled) {
          const { isValid: codeIsValid, message } = this.validateRevealCode(this.state.hsRevealCode);
          if (!codeIsValid) {
            isValid = false;
            errorMessage = message;
          }
        }
        break;
      default:
        break;
    }

    if (errorMessage != "") {
      // console.log('here');
      this.props.root.addNotification(errorMessage, false);
    }

    return isValid;
  }

  onUploadSuccess(res) {
    console.log("upload success: " + res['file_id']);
    let fileID = res['file_id'];
    let filePath = res['path'] ? Config.UPLOAD_PATH() + res['path'] : "";
    let previewPath = res['tn_path'] ? Config.UPLOAD_PATH() + res['tn_path'] : "";
    
    this.setState({
      localeCoverFileID: fileID,
      localeCoverFilePath: filePath,
      localeCoverPreviewPath: previewPath,
      localeCoverRawPath: res['path'] || "",
      localeCoverRawPreviewPath: res['tn_path'] || ""
    });
    this.state.localeCoverFileID = fileID;
    this.state.localeCoverFilePath = filePath;
    this.state.localeCoverPreviewPath = previewPath;
    this.state.localeCoverRawPath = res['path'] || "";
    this.state.localeCoverRawPreviewPath = res['tn_path'] || "";
  }

  onAbortUpload() {
    this.setState({
      localeCoverFileID: "",
      localeCoverFilePath: "",
      localeCoverPreviewPath: "",
      localeCoverRawPath: "",
      localeCoverRawPreviewPath: ""
    });
    this.state.localeCoverFileID = "";
    this.state.localeCoverFilePath = "";
    this.state.localeCoverPreviewPath = "";
    this.state.localeCoverRawPath = "";
    this.state.localeCoverRawPreviewPath = "";
  }

  onMapMounted = (map) => {
    this.state.map = map;
  }

  onRectangleMounted(){
    if (this.refs.localeDetailMapBar) {
      this.refs.localeDetailMapBar.updateMapSize();
    }
  }

  onBoundsChanged() {
    if (this.props.parent.onBoundsChanged) {
      this.props.parent.onBoundsChanged();
    }
    if (this.refs.localeDetailMapBar) {
      this.refs.localeDetailMapBar.updateMapSize();
    }
    this.updateTeleportButtonDisabledState();
  }

  setFrameFromVisibleMap() {
    if (this._isMounted === false || !this.state.webGLSupported || this.state.activeView !== 1) {
      return;
    }

    const bounds = this.syncVisibleMapBounds(this.state.viewState);
    if (!bounds) {
      // Map not ready yet, retry. Store the timer id so it can be cleared on unmount
      // (prevents the retry loop from leaking and calling setState after unmount).
      if (this.frameFromVisibleMapTimer) {
        clearTimeout(this.frameFromVisibleMapTimer);
      }
      this.frameFromVisibleMapTimer = setTimeout(() => {
        this.frameFromVisibleMapTimer = null;
        this.setFrameFromVisibleMap();
      }, 200);
      return;
    }

    this.applyFrameFromMapBounds(bounds);
  }

  applyFrameFromMapBounds(mapBounds = this.state.map.bounds) {
    // Apply the frame calculation logic (same as localeMatchViewClicked)
    if (!this.hasUsableBounds(mapBounds)) {
      return;
    }

    let bounds = mapBounds;
    let upperLeftLatLon = {
      lat: bounds.ul.lat,
      lng: bounds.ul.lon,
    };
    let bottomRightLatLon = {
      lat: bounds.br.lat,
      lng: bounds.br.lon,
    }
    let upperLeftInMeter = this.latLonToMeter(upperLeftLatLon.lat, upperLeftLatLon.lng);
    let bottomRightInMeter = this.latLonToMeter(bottomRightLatLon.lat, bottomRightLatLon.lng);
    let heightBorder = (upperLeftInMeter.y - bottomRightInMeter.y) * 0.1;
    upperLeftInMeter.y -= heightBorder;
    bottomRightInMeter.y += heightBorder;
    let centerInMeterX = (upperLeftInMeter.x + bottomRightInMeter.x) / 2.0;
    let centerInMeterY = (upperLeftInMeter.y + bottomRightInMeter.y) / 2.0;
    let width = upperLeftInMeter.y - bottomRightInMeter.y;
    // The visible-area-derived square can be smaller than the minimum allowed field
    // trip area (e.g. ~953 m at the default zoom), which starts the user below
    // MinLocaleMapSize (1 km): it trips the "too narrow" warning and blocks the Next
    // button (checkLocaleBoundary). Floor the default frame at the minimum so it is
    // always at least 1 km x 1 km. The +1 m is a safety margin: this frame is stored
    // as lat/lon and later re-measured in Web Mercator meters with parseInt truncation
    // and a strict `< MinLocaleMapSize` check, so a frame clamped to exactly 1000 m
    // round-trips to ~999.99 m and would still read as 999 m / fail the size check.
    width = Math.max(width, MinLocaleMapSize + 1);
    // Center a square of side `width` on the computed center on both axes, so the box
    // stays square and centered even when the width was clamped up to the minimum.
    upperLeftInMeter.x = centerInMeterX - width / 2.0;
    bottomRightInMeter.x = centerInMeterX + width / 2.0;
    upperLeftInMeter.y = centerInMeterY + width / 2.0;
    bottomRightInMeter.y = centerInMeterY - width / 2.0;

    upperLeftLatLon = this.meterToLatLon(upperLeftInMeter.x, upperLeftInMeter.y);
    bottomRightLatLon = this.meterToLatLon(bottomRightInMeter.x, bottomRightInMeter.y);
    let upperLeft = {
      lat: upperLeftLatLon.lat,
      lng: upperLeftLatLon.lon,
    };
    let bottomRight = {
      lat: bottomRightLatLon.lat,
      lng: bottomRightLatLon.lon,
    };

    let centerLatLon = this.meterToLatLon(centerInMeterX, centerInMeterY);
    let center = {
      lat: centerLatLon.lat,
      lng: centerLatLon.lon,
    };

    const rectangleState = this.getRectangleState(upperLeft, bottomRight, center);
    Object.assign(this.state, rectangleState);
    
    // Force re-render to show the updated frame
    this.setState(rectangleState, () => {
      this.onBoundsChanged();
      this.updateTeleportButtonDisabledState();
    });
  }

  getRectangleState(upperLeft, bottomRight, center) {
    return {
      localeCenter: center,
      localeUpperLeft: upperLeft,
      localeBottomRight: bottomRight,
      boxData: {
        type: "FeatureCollection",
        features:[{
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [bottomRight.lng, bottomRight.lat],
                [bottomRight.lng, upperLeft.lat],
                [upperLeft.lng, upperLeft.lat],
                [upperLeft.lng, bottomRight.lat],
                [bottomRight.lng, bottomRight.lat]
              ]
            ]
          }
        }]
      },
      localeRect: {
        bounds:{
          ul:{
            lat: upperLeft.lat,
            lon: upperLeft.lng
          },
          br:{
            lat: bottomRight.lat,
            lon: bottomRight.lng
          },
          center:{
            lat: center.lat,
            lon: center.lng
          }
        }
      }
    };
  }

  localeMatchViewClicked() {
    let bounds = this.syncVisibleMapBounds(this.state.viewState);
    if (!bounds) {
      return;
    }
    let upperLeftLatLon = {
      lat: bounds.ul.lat,
      lng: bounds.ul.lon,
    };
    let bottomRightLatLon = {
      lat: bounds.br.lat,
      lng: bounds.br.lon,
    }
    let upperLeftInMeter = this.latLonToMeter(upperLeftLatLon.lat, upperLeftLatLon.lng);
    let bottomRightInMeter = this.latLonToMeter(bottomRightLatLon.lat, bottomRightLatLon.lng);
    let heightBorder = (upperLeftInMeter.y - bottomRightInMeter.y) * 0.1;
    upperLeftInMeter.y -= heightBorder;
    bottomRightInMeter.y += heightBorder;
    let centerInMeterX = (upperLeftInMeter.x + bottomRightInMeter.x) / 2.0;
    let centerInMeterY = (upperLeftInMeter.y + bottomRightInMeter.y) / 2.0;
    let width = upperLeftInMeter.y - bottomRightInMeter.y;
    upperLeftInMeter.x = centerInMeterX - width / 2.0;
    bottomRightInMeter.x = centerInMeterX + width / 2.0;

    upperLeftLatLon = this.meterToLatLon(upperLeftInMeter.x, upperLeftInMeter.y);
    bottomRightLatLon = this.meterToLatLon(bottomRightInMeter.x, bottomRightInMeter.y);
    let upperLeft = {
      lat: upperLeftLatLon.lat,
      lng: upperLeftLatLon.lon,
    };
    let bottomRight = {
      lat: bottomRightLatLon.lat,
      lng: bottomRightLatLon.lon,
    };

    let centerLatLon = this.meterToLatLon(centerInMeterX, centerInMeterY);
    let center = {
      lat: centerLatLon.lat,
      lng: centerLatLon.lon,
    };

    const rectangleState = this.getRectangleState(upperLeft, bottomRight, center);
    const nextState = {
      ...rectangleState,
      viewState: {
        ...this.state.viewState,
        longitude: center.lng,
        latitude: center.lat,
        transitionDuration: 1000,
        transitionInterpolator: this.state.flyContainer
      }
    };
    Object.assign(this.state, nextState);
    this.setState(nextState, () => {
      this.onBoundsChanged();
      this.updateTeleportButtonDisabledState();
    });
  }

  localeTeleportClicked() {
    let bounds = this.state.localeRect.bounds;
    let position = {
      lat: (bounds.ul.lat + bounds.br.lat) / 2.0,
      lng: (bounds.ul.lon + bounds.br.lon) / 2.0,
    }
    this.mapPanTo(position);
  }

  mapPanTo(position, zoom=13) {
    this.setState({
      viewState: {
        ...this.state.viewState,
        longitude: position.lng,
        latitude: position.lat,
        zoom: zoom,
        transitionDuration: 1000,
        transitionInterpolator: this.state.flyContainer
      }
    });
    this.state.map.zoom = zoom;
    this.onZoomChanged();
    this.updateMapBound({
      ...this.state.viewState,
      longitude: position.lng,
      latitude: position.lat,
      zoom: zoom,
    });
  }

  updateMapState() {
    let zoom = this.state.map.zoom;
    let bounds = this.state.localeRect.bounds;
    let center = {
      lat: bounds.center.lat,
      lng: bounds.center.lon,
    }
    let upperLeft = {
      lat: bounds.ul.lat,
      lng: bounds.ul.lon,
    }
    let bottomRight = {
      lat: bounds.br.lat,
      lng: bounds.br.lon,
    }
    this.setState({
      localeCenter: center,
      zoom: zoom,
      localeUpperLeft: upperLeft,
      localeBottomRight: bottomRight,
    })
    this.state.localeCenter = center;
    this.state.zoom = zoom;
    this.state.localeUpperLeft = upperLeft;
    this.state.localeBottomRight = bottomRight;
  }

  updateMapZoom(zoom){
    if (zoom == this.state.viewState.zoom){
      return;
    }
    let position = {
      lat: this.state.viewState.latitude,
      lng: this.state.viewState.longitude
    };
    this.mapPanTo(position, zoom);
    this.updateMapBound(this.state.viewState);
  }

  updateMapBound(viewState){
    const bounds = this.syncVisibleMapBounds(viewState);
    if (bounds) {
      this.updateTeleportButtonDisabledState();
    }
  }

  latLonToMeter(lat, lon) {
    let originShift = 2 * Math.PI * 6378137 / 2.0;
    let x = lon * originShift / 180.0;
    let y = Math.log(Math.tan( (90.0 + lat) * Math.PI / 360.0) ) / (Math.PI / 180.0);
    y = y * originShift / 180.0;
    let result = { x: x, y: y };
    return result;
  }

  meterToLatLon(x, y) {
    let originShift = 2 * Math.PI * 6378137 / 2.0;
    let lon = (x / originShift) * 180.0;
    let lat = (y / originShift) * 180.0;
    lat = 180.0 / Math.PI * (2 * Math.atan (Math.exp(lat * Math.PI / 180.0) ) - Math.PI / 2.0);
    let result = { lat: lat, lon: lon };
    return result;
  }

  checkLocaleBoundary() {
    // result 0 = OK, -1 = too small, 1 = too big
    let result = 0;
    let bounds = this.state.localeRect.bounds;
    let zoom = this.state.map.zoom;
    let upperLeft = this.latLonToMeter(bounds.ul.lat, bounds.ul.lon);
    let bottomRight = this.latLonToMeter(bounds.br.lat, bounds.br.lon);
    if (Math.abs(bottomRight.x - upperLeft.x) < MinLocaleMapSize || Math.abs(upperLeft.y - bottomRight.y) < MinLocaleMapSize) {
      result = -1;
    }
    if (Math.abs(bottomRight.x - upperLeft.x) > MaxLocaleMapSize || Math.abs(upperLeft.y - bottomRight.y) > MaxLocaleMapSize) {
      result = 1;
    }
    return result;
  }

  formatDistance(dist) {
    if (dist >= 1000) {
      return (dist / 1000).toFixed(2) + 'km';
    }
    return Math.round(dist) + 'm';
  }

  onZoomChanged() {
    let zoom = this.state.map.zoom;
    this.refs.localeDetailMapBar.setState({ zoom: zoom });

    let reminder = "";
    /*if (zoom < 15){
      reminder = Localization.strings.LOCALE_MAP_ZOOM_LV_REMINDER;
    }
    this.refs.localeDetailMapBar.setState({mapZoomReminder: reminder});
    this.refs.localeDetailMapBar.state.mapZoomReminder = reminder;*/
  }

  onEdit({ updatedData, editType, editContext }) {
    if (editType === 'movePosition' || editType === 'finishMovePosition' || editType === 'scaling' || editType === 'scaled' || editType === 'translating' || editType === 'translated') {
      const normalized = this.normalizeRectangleData(updatedData);
      const isDragEnd = editType === 'finishMovePosition' || editType === 'scaled' || editType === 'translated';
      this.isBoxEditing = !isDragEnd;
      // [FTA-DEBUG] temporary diagnostic: shows whether live drag edits fire and which
      // update path runs. Remove once the drag-follow issue is resolved.
      console.log('[FTA] onEdit type=' + editType + ' deckReady=' + !!(this.deckGLRef && this.deckGLRef.deck));

      if (isDragEnd) {
        // Commit the final position through React (also restores the dimension labels and
        // re-enables picking). Cancel any pending live-drag frame first so a stale frame
        // can't overwrite the committed position.
        if (this.pendingEditFrame) {
          window.cancelAnimationFrame(this.pendingEditFrame);
          this.pendingEditFrame = null;
        }
        this.pendingBoxData = null;
        this.applyBoxDataAndDerivedState(normalized);
        return;
      }

      // Live drag: update ONLY the box layer on the existing deck instance, throttled to
      // one update per animation frame, so the frame tracks the cursor smoothly. Doing a
      // full React re-render of the whole map panel (DeckGL + Mapbox + toolbar + sliders)
      // on every drag frame is too heavy on 4K displays -- the frame lags behind and looks
      // like it only jumps to the new position on release. Falls back to setState if the
      // deck handle isn't available.
      this.pendingBoxData = normalized;
      if (this.pendingEditFrame) {
        return;
      }
      this.pendingEditFrame = window.requestAnimationFrame(() => {
        this.pendingEditFrame = null;
        const nextBoxData = this.pendingBoxData;
        this.pendingBoxData = null;
        if (!nextBoxData) {
          return;
        }
        const deck = this.deckGLRef && this.deckGLRef.deck;
        // [FTA-DEBUG] temporary diagnostic.
        console.log('[FTA] rAF applied via ' + ((deck && this.isBoxEditing) ? 'deck.setProps' : 'setState'));
        if (deck && this.isBoxEditing) {
          // Keep state in sync (without a re-render) so any unrelated render during the
          // drag stays consistent, then update only the box layer on the deck instance.
          // While editing, the dimension labels are hidden, so the only layer is the box.
          this.state.boxData = nextBoxData;
          deck.setProps({ layers: [this.genEditableLayer(nextBoxData)] });
        } else {
          this.setState({ boxData: nextBoxData });
        }
      });
    }
  }

  normalizeRectangleData(updatedData) {
    const coords = updatedData.features[0].geometry.coordinates[0];

    const lons = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const newCoordinates = [
      [maxLon, minLat],
      [maxLon, maxLat],
      [minLon, maxLat],
      [minLon, minLat],
      [maxLon, minLat]
    ];

    return {
      ...updatedData,
      features: updatedData.features.map((feature, index) => {
        if (index !== 0) {
          return feature;
        }
        return {
          ...feature,
          geometry: {
            ...feature.geometry,
            coordinates: [newCoordinates]
          }
        };
      })
    };
  }

  applyBoxDataAndDerivedState(newBoxData) {
    const coords = newBoxData.features[0].geometry.coordinates[0];
    const upperLeft = { lng: coords[2][0], lat: coords[2][1] };
    const bottomRight = { lng: coords[0][0], lat: coords[0][1] };
    const center = { lng: (upperLeft.lng + bottomRight.lng) / 2, lat: (upperLeft.lat + bottomRight.lat) / 2 };

    this.setState({
      boxData: newBoxData,
      localeUpperLeft: upperLeft,
      localeBottomRight: bottomRight,
      localeCenter: center,
      localeRect: {
        bounds: {
          ul: {
            lat: upperLeft.lat,
            lon: upperLeft.lng
          },
          br: {
            lat: bottomRight.lat,
            lon: bottomRight.lng
          },
          center: {
            lat: center.lat,
            lon: center.lng
          }
        }
      }
    }, () => {
      this.onBoundsChanged();
      this.updateTeleportButtonDisabledState();
    });
  }

  hexToRgba(hex, alphaSuffix) {
    hex = hex.padStart(6, '0').slice(-6);
    hex = hex + alphaSuffix;
    return hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16));
  }

  getBoxFillColor = () => {
    return this.hexToRgba(this.state.localeColor, '30');
  }

  getBoxLineColor = () => {
    return this.hexToRgba(this.state.localeColor, 'ff');
  }

  genEditableLayer(boxData = this.state.boxData){
    const selectedFeatureIndexes = [0];

    // The field trip area is dragged/expanded/reduced by grabbing the box body
    // (TranslateMode) or the red corner handles (ScaleMode). Both rely on deck.gl's
    // picking landing on the target within `pickingRadius`. The original handles
    // (radius 4-8px) and tight pickingRadius (10) were very hard to hit on dense
    // displays such as Windows 4K, where users reported the box being hard to drag,
    // expand, or reduce. Use comfortably large handles and a more forgiving grab
    // tolerance so the targets are easy to catch regardless of display density.
    const editableLayer = new DragOptimizedEditableGeoJsonLayer({
      id: 'geojson-layer',
      data: boxData,
      mode: this.editModes,
      pickingRadius: 18,
      // Performance: the per-event GPU pick during a drag is skipped by
      // DragOptimizedEditableGeoJsonLayer.getPicks (see above). This pickable flag is a
      // lightweight secondary guard that also stops picking once a drag is underway.
      pickable: !this.isBoxEditing,
      selectedFeatureIndexes,
      _subLayerProps: {
        guides: {
          pointType: 'circle',
          _subLayerProps: {
            'points-circle': {
              // Styling for editHandles goes here
              type: ScatterplotLayer,
              radiusScale: 1,
              stroked: true,
              getLineWidth: 2,
              lineWidthUnits: 'pixels',
              lineWidthMinPixels: 2,
              radiusMinPixels: 9,
              radiusMaxPixels: 14,
              getRadius: 6,
              getFillColor: [220, 10, 0],
              getLineColor: [250, 250, 250],
            },
          },
        },
        geojson: {
          getFillColor: this.getBoxFillColor,
          getLineColor: this.getBoxLineColor,
        },
      },
      onEdit: this.onEdit
    });

    return editableLayer;
  }

  genTextLayers(){
    const layers = [];
    var charSet = 'auto';

    let coor = this.state.boxData.features[0].geometry.coordinates;
    let upperLeft = this.latLonToMeter(coor[0][2][1], coor[0][2][0]);
    let bottomRight = this.latLonToMeter(coor[0][0][1], coor[0][0][0]);
    let width = parseInt(Math.abs(bottomRight.x - upperLeft.x));
    let height = parseInt(Math.abs(upperLeft.y - bottomRight.y));
    
    const dimensionData = [
      {
        name: ` ${this.formatDistance(height)} `,
        coordinates: [coor[0][0][0], (coor[0][0][1] + coor[0][2][1]) / 2],
        anchor: 'start',
        baseline: 'center',
        pixelOffset: [5, 0]
      },
      {
        name: ` ${this.formatDistance(width)} `,
        coordinates: [(coor[0][2][0] + coor[0][0][0]) / 2, coor[0][2][1]],
        anchor: 'middle',
        baseline: 'bottom', 
        pixelOffset: [0, -5]
      }
    ];

    layers.push(new TextLayer({
      id: 'dimensions-layer',
      data: dimensionData,
      characterSet: 'auto',
      background: true,
      backgroundPadding: [8, 4, 8, 4],
      getBackgroundColor: [51, 51, 51, 179],
      getColor: [255, 255, 255],
      getPosition: d => d.coordinates,
      getText: d => d.name,
      getTextAnchor: d => d.anchor,
      getAlignmentBaseline: d => d.baseline,
      getPixelOffset: d => d.pixelOffset,
      getSize: 16,
      pickable: false,
    }));

    return layers;
  }

  renderWarningOverlay() {
    let warningMessage = '';
    let coor = this.state.boxData.features[0].geometry.coordinates;
    let upperLeft = this.latLonToMeter(coor[0][2][1], coor[0][2][0]);
    let bottomRight = this.latLonToMeter(coor[0][0][1], coor[0][0][0]);
    let width = parseInt(Math.abs(bottomRight.x - upperLeft.x));
    let height = parseInt(Math.abs(upperLeft.y - bottomRight.y));
    
    if (this.state.viewState.zoom < 14) {
      warningMessage = Localization.strings.LOCALE_MAP_ZOOM_LV_REMINDER;
    } else if (width > MaxLocaleMapSize || height > MaxLocaleMapSize) {
      warningMessage = Localization.strings.TOO_WIDE_REMINDER;
    } else if (width < MinLocaleMapSize || height < MinLocaleMapSize) {
      warningMessage = Localization.strings.TOO_NARROW_REMINDER;
    }

    if (!warningMessage) {
      return null;
    }

    return (
      <div style={{
        position: 'absolute',
        top: 10,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1,
        padding: '8px',
        backgroundColor: 'rgba(156, 0, 6, 0.8)',
        color: 'white',
        borderRadius: '4px',
        fontSize: '16px',
        pointerEvents: 'none'
      }}>
        {`\u26A0 ${warningMessage}`}
      </div>
    );
  }

  render() {
    const klaData = this.props.root.refs.userDataProfile.state.kla.map((row, i) => ({
      value: String(i),
      label: row[Localization.strings.getLanguage() === "en" ? 'name_eng' : 'name']
    }));
    const subjectData = [
      { value: "0", label: Localization.strings.UNSPECIFIED_SUBJECT },
      ...this.props.root.refs.userDataProfile.state.subject.reduce((acc, row, i) => {
        if (this.state.localeKLA >= 0 && parseInt(row['kla_id']) === this.state.localeKLA) {
          acc.push({ value: String(i + 1), label: row[Localization.strings.getLanguage() === "en" ? 'name_eng' : 'name'] });
        }
        return acc;
      }, [])
    ];

    let localeDetails = (
      <SimpleGrid cols={1} spacing="md">
        <TextInput
          label={Localization.strings.NAME}
          value={this.state.localeName}
          onChange={this.localeNameChanged}
          maxLength={Config.MAX_TEXT_LEN().NAME}
          style={{ width: '80%' }}
        />
        <Textarea
          label={Localization.strings.DESCRIPTION}
          value={this.state.localeDesc}
          onChange={this.localeDescChanged}
          autosize
          minRows={3}
          maxLength={Config.MAX_TEXT_LEN().CONTENT}
          style={{ width: '80%' }}
        />
        <Select
          label={Localization.strings.KLA_SHORT}
          placeholder={Localization.strings.PLEASE_SELECT}
          value={this.state.localeKLA >= 0 ? String(this.state.localeKLA) : null}
          onChange={(v) => this.localeKLAChanged({ target: { value: Number(v) } })}
          data={klaData}
          allowDeselect={false}
          style={{ width: '80%' }}
        />
        <Select
          label={Localization.strings.SUBJECT}
          placeholder={Localization.strings.PLEASE_SELECT}
          value={this.state.localeSubject >= 0 ? String(this.state.localeSubject) : null}
          onChange={(v) => this.localeSubjectChanged({ target: { value: Number(v) } })}
          data={subjectData}
          allowDeselect={false}
          style={{ width: '80%' }}
        />
      </SimpleGrid>
    );

    const initialViewState = {
      longitude: this.state.localeCenter.lng,
      latitude: this.state.localeCenter.lat,
      zoom: this.state.zoom
    };
    const { viewState, map } = this.state;

    const shouldRenderMap = this.state.activeView === 1 && this.state.webGLSupported;
    const editableLayers = shouldRenderMap ? this.genEditableLayer() : null;
    const textLayers = shouldRenderMap && !this.isBoxEditing ? this.genTextLayers() : [];
    const warningOverlay = shouldRenderMap && !this.isBoxEditing ? this.renderWarningOverlay() : null;
    const devicePixels = this.getDevicePixels();

    return (
      <div style={{margin: "12px 0 0 0", minHeight: "70vh"}}>
        <Switch condition={this.state.activeView}>
          <Case value={0}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <div>
                {localeDetails}
              </div>
              <div>
                <FilePicker
                  root={this.props.root}
                  parent={this}
                  onUploadSuccess={this.onUploadSuccess}
                  onAbortUpload={this.onAbortUpload}
                  epackID={this.props.epackID}
                  allowedExtension="jpg,jpeg,png"
                  pickerTitle={Localization.strings.COVER_IMAGE}
                  fileType="image"
                  fileID={this.state.localeCoverFileID}
                  filePath={this.state.localeCoverFilePath}
                  previewPath={this.state.localeCoverPreviewPath}
                  sizeLimit={10 * oneMB}
                  ref="filePicker"
                />
              </div>
            </SimpleGrid>
          </Case>
          <Case value={1}>
            <LocaleDetailMapBar root={this.props.root} parent={this} updateMapZoom={this.updateMapZoom} ref="localeDetailMapBar" isTeleportButtonDisabled={this.state.isTeleportButtonDisabled}/>

            {!this.state.webGLSupported ? this.renderMapFallback() : (
              <LocaleMapStepErrorBoundary
                resetKey={`${this.state.activeView}-${this.state.map._style}`}
                onError={this.handleMapRenderError}
                fallback={this.renderMapFallback()}
              >
                <div style={{padding:'8px 0 0 0'}}>
                  <div
                      ref={this.mapContainerRef}
                      style={{position:"relative", height:'57vh', minHeight: 320}}>
                    <DeckGL 
                      initialViewState={initialViewState} 
                      viewState={viewState}
                      useDevicePixels={devicePixels}
                      controller={{
                        doubleClickZoom: false
                      }} 
                      height="100%"
                      width="100%"
                      position="absolute"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0
                      }}
                      layers={[editableLayers, ...textLayers]}
                      // Drive the cursor from the edit layer so it changes when the pointer is
                      // within the picking radius of the field trip area: a "move" arrow over the
                      // box body and diagonal "resize" arrows over the corner handles, signalling
                      // that the box can be dragged / expanded / reduced there. Falls back to the
                      // edit layer's default (grab/grabbing) elsewhere. Guard against the layer
                      // being null (e.g. when the map view is not active).
                      getCursor={editableLayers ? editableLayers.getCursor.bind(editableLayers) : undefined}
                      ref={this.handleDeckRef}
                      onError={this.handleMapRenderError}
                      onViewStateChange={(viewState =>{
                        this.setState({
                          viewState: {
                            ...this.state.viewState,
                            zoom: viewState.viewState.zoom,
                            longitude: viewState.viewState.longitude,
                            latitude: viewState.viewState.latitude,
                            transitionDuration: 0,
                            transitionInterpolator: null
                          }
                        });
                        if(viewState.interactionState.isZooming){
                          this.state.map.zoom = parseInt(viewState.viewState.zoom);
                          this.onZoomChanged();
                        }
                        if (!viewState.interactionState.isDragging && !viewState.interactionState.isRotating || viewState.interactionState.isZooming){
                          this.updateMapBound(viewState.viewState);
                        }
                      })}
                    >
                        <Map
                          ref={this.mapRef}
                          mapStyle={map.style}
                          mapboxAccessToken={Config.MBOX_KEY()}
                          attributionControl={false}
                          pixelRatio={devicePixels}
                          style={{width: "100%", height: "100%"}}
                        />
                        {warningOverlay}
                    <Button.Group style={{top:10, left:10, position:"relative"}}>
                      <Button size="xs" variant={this.state.map._style=="mono"? "default":"filled"} onClick={
                        () => {
                          this.setState({
                            map:{
                              ...this.state.map,
                              style: Config.MBOX_STYLE(Localization.strings.getLanguage()).mono,
                              _style: "mono"
                            }
                          });
                          this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).mono;
                          this.state.map._style = "mono";
                        }
                      }>{Localization.strings.MAP_MONO}</Button>
                      <Button size="xs" variant={this.state.map._style=="satellite"? "default":"filled"} onClick={
                        () => {
                          this.setState({
                            map:{
                              ...this.state.map,
                              style: Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite,
                              _style: "satellite"
                            }
                          });
                          this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite;
                          this.state.map._style = "satellite";
                        }
                      }>{Localization.strings.MAP_SATELLITE}</Button>
                      <Button size="xs" variant={this.state.map._style=="cartoon"? "default":"filled"} onClick={
                        () => {
                          this.setState({
                            map:{
                              ...this.state.map,
                              style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
                              _style: "cartoon"
                            }
                          });
                          this.state.map.style = Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon;
                          this.state.map._style = "cartoon";
                        }
                      }>{Localization.strings.MAP_VECTOR}</Button>
                    </Button.Group>
                    <div 
                      style={{
                        position: 'absolute', 
                        top: 50, 
                        left: 10, 
                        zIndex: 1, 
                        width: 'auto', 
                        backgroundColor: 'rgba(255, 255, 255, 0.3)', 
                        borderRadius: '4px',
                        padding: '5px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                      }}
                      onPointerDown={e => e.stopPropagation()}
                      onPointerMove={e => e.stopPropagation()}
                      onContextMenu={e => e.preventDefault()}
                    >
                      <ActionIcon variant="subtle" onClick={this.zoomIn} size="md">
                        <IconPlus size={18} />
                      </ActionIcon>
                      <VerticalZoomSlider
                        value={Math.round(this.state.viewState.zoom)}
                        min={this.state.viewState.minZoom}
                        max={this.state.viewState.maxZoom}
                        step={1}
                        onChange={(value) => {
                          this.setState({
                            viewState: {
                              ...this.state.viewState,
                              zoom: value
                            }
                          });
                        }}
                        onChangeEnd={(value) => this.updateMapZoom(value)}
                      />
                      <ActionIcon variant="subtle" onClick={this.zoomOut} size="md">
                        <IconMinus size={18} />
                      </ActionIcon>
                    </div>
                    </DeckGL>
                  </div>
                </div>
              </LocaleMapStepErrorBoundary>
            )}
          </Case>
          <Case value={2}>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              {/* Left column. Stack, not a nested single-column SimpleGrid: a
                  nested grid stretches its auto rows to fill the height of the
                  taller column (align-content defaults to stretch), which is
                  what opened the empty bands between Language, Sharable and
                  Publish. A Stack packs its children from the top. */}
              <Stack gap="md">
                <Select
                  label={Localization.strings.LANGUAGE}
                  value={this.state.localeLanguage}
                  onChange={(v) => this.localeLanguageChanged({ target: { value: v } })}
                  data={[
                    { value: "tc", label: "繁體中文" },
                    { value: "sc", label: "简体中文" },
                    { value: "en", label: "English" },
                  ]}
                  allowDeselect={false}
                  style={SETTING_FIELD_STYLE}
                />
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <Select
                    label={Localization.strings.SHARABLE}
                    value={String(this.state.localeShareable)}
                    onChange={(v) => this.localeShareableChanged({ target: { value: v === "true" } })}
                    disabled={this.props.isDuplicate}
                    data={[
                      { value: "true", label: Localization.strings.PUBLIC },
                      { value: "false", label: Localization.strings.SCHOOL_ONLY_USE },
                    ]}
                    allowDeselect={false}
                    error={this.props.isDuplicate ? Localization.strings.DUPLICATION_NOT_ALLOWED_HINT : undefined}
                    style={SETTING_FIELD_STYLE}
                  />
                  <Tooltip label={Localization.strings.SHAREABLE_HINT} position="bottom-end" withArrow>
                    <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                  </Tooltip>
                </Group>
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <Select
                    label={Localization.strings.PUBLISH}
                    value={String(this.state.localePublish)}
                    onChange={(v) => this.localePublishChanged({ target: { value: v === "true" } })}
                    data={[
                      { value: "true", label: Localization.strings.YES },
                      { value: "false", label: Localization.strings.NO },
                    ]}
                    allowDeselect={false}
                    style={SETTING_FIELD_STYLE}
                  />
                  <Tooltip label={Localization.strings.PUBLISH_HINT} position="bottom-end" withArrow>
                    <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                  </Tooltip>
                </Group>
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <Select
                    label={Localization.strings.DUPLICATION_RULE}
                    value={String(this.state.localeDuplicationAllowed)}
                    onChange={(v) => this.localeDuplicationChanged({ target: { value: Number(v) } })}
                    data={[
                      { value: "0", label: Localization.strings.DUPLICATION_RULE_0 },
                      { value: "1", label: Localization.strings.DUPLICATION_RULE_1 },
                      { value: "2", label: Localization.strings.DUPLICATION_RULE_2 },
                      { value: "3", label: Localization.strings.DUPLICATION_RULE_3 },
                    ]}
                    allowDeselect={false}
                    style={SETTING_FIELD_STYLE}
                  />
                  <Tooltip label={Localization.strings.DUPLICATION_RULE_HINT} position="bottom-end" withArrow>
                    <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                  </Tooltip>
                </Group>
                <div>
                  <Text size="sm" fw={500} mb={4}>{Localization.strings.COLOR}</Text>
                  <Group gap="xs" align="center" wrap="nowrap">
                    <Popover
                      opened={Boolean(this.state.colorPickerAnchor)}
                      onChange={(opened) => { if (!opened) { this.handleColorPickerClose(); } }}
                      position="bottom-start"
                    >
                      <Popover.Target>
                        <Button
                          onClick={this.handleColorPickerClick}
                          variant="default"
                          style={{
                            ...SETTING_FIELD_STYLE,
                            height: '40px',
                            justifyContent: 'flex-start',
                            textTransform: 'none'
                          }}
                        >
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: `#${this.state.localeColor}`,
                            border: '1px solid #ccc',
                            marginRight: '8px'
                          }} />
                        </Button>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <div style={{ padding: '1rem' }}>
                          <CirclePicker
                            color={this.state.localeColor}
                            onChange={(color) => {
                              this.localeColorChanged(color);
                              this.handleColorPickerClose();
                            }}
                          />
                        </div>
                      </Popover.Dropdown>
                    </Popover>
                    <Tooltip label={Localization.strings.LOCALE_COLOR_REMINDER} position="bottom-end" withArrow>
                      <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                    </Tooltip>
                  </Group>
                </div>

                {/* QR code lives with the other sharing controls rather than at
                    the bottom of the avatar-interaction column, which also
                    evens out the two columns. Edit-only: there is no locale to
                    encode yet while creating one. */}
                {this.props.root.getCurrentLocale() && (
                  <div>
                    <Text size="sm" fw={500} mb={4}>{Localization.strings.QR_CODE}</Text>
                    <Group gap="xs" align="center" wrap="nowrap">
                      <Button
                        variant="default"
                        leftSection={<IconQrcode size={18} />}
                        onClick={() => this.setState({ qrDialogOpen: true })}
                        style={SETTING_FIELD_STYLE}
                      >
                        {Localization.strings.QR_CODE}
                      </Button>
                      <Tooltip label={Localization.strings.QR_CODE_DESC} position="bottom-end" withArrow>
                        <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                      </Tooltip>
                    </Group>
                  </div>
                )}
              </Stack>

              {/* Right column */}
              <Stack gap="md">
                {/* <If condition={this.props.root.getMySchoolId() == 3}> */}
                <If condition={true}>
                  <Then>
                    <Group gap="xs" align="flex-end" wrap="nowrap">
                      <Select
                        label={this.renderAppVersionWarningLabel(
                          Localization.strings.USE_REVEAL_CODE,
                          Localization.strings.NEW_APP_VERSION_REQUIRED
                        )}
                        value={String(this.state.hsRevealCodeEnabled)}
                        onChange={(v) => this.enableRevealCodeChanged({ target: { value: v === "true" } })}
                        data={[
                          { value: "true", label: Localization.strings.YES },
                          { value: "false", label: Localization.strings.NO },
                        ]}
                        allowDeselect={false}
                        style={SETTING_FIELD_STYLE}
                      />
                      <Tooltip label={Localization.strings.REVEAL_CODE_HINT} position="bottom-end" withArrow>
                        <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                      </Tooltip>
                    </Group>

                    <If condition={this.state.hsRevealCodeEnabled}>
                      <Then>
                        <Group gap="xs" align="flex-end" wrap="nowrap">
                          <TextInput
                            label={Localization.strings.REVEAL_CODE}
                            value={this.state.hsRevealCode}
                            onChange={this.revealCodeChanged}
                            maxLength={Config.MAX_TEXT_LEN().REVEAL_CODE}
                            style={SETTING_FIELD_STYLE}
                          />
                          <Tooltip label={Localization.strings.REVEAL_CODE_REQUIREMENT_1} position="bottom-end" withArrow>
                            <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                          </Tooltip>
                        </Group>
                      </Then>
                    </If>

                  </Then>
                </If>

                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <Select
                    label={Localization.strings.AI_ASSESSMENT}
                    value={String(this.state.manualAiAssessment)}
                    onChange={(v) => this.manualAiAssessmentChanged({ target: { value: Number(v) } })}
                    data={[
                      { value: "1", label: Localization.strings.MANUALLY },
                      { value: "0", label: Localization.strings.AUTOMATICALLY },
                    ]}
                    allowDeselect={false}
                    style={SETTING_FIELD_STYLE}
                  />
                  <Tooltip label={Localization.strings.AI_ASSESSMENT_MODE_HELP} position="bottom-end" withArrow>
                    <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                  </Tooltip>
                </Group>

                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <Select
                    label={Localization.strings.AUTO_SHARE_ASSESSMENT}
                    value={String(this.state.autoShareAssessment)}
                    onChange={(v) => this.autoShareAssessmentChanged({ target: { value: Number(v) } })}
                    data={[
                      { value: "0", label: Localization.strings.NO },
                      { value: "1", label: Localization.strings.AUTO_SHARE_VIA_DEVICE },
                      { value: "2", label: Localization.strings.AUTO_SHARE_VIA_EDCITY },
                      { value: "3", label: Localization.strings.AUTO_SHARE_VIA_BOTH },
                    ]}
                    allowDeselect={false}
                    style={SETTING_FIELD_STYLE}
                  />
                  <Tooltip label={Localization.strings.AUTO_SHARE_ASSESSMENT_HELP} position="bottom-end" withArrow>
                    <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                  </Tooltip>
                </Group>
              </Stack>
            </SimpleGrid>

            {/* Full-width responsive grid keeps the five related avatar
                interaction options compact and aligned. */}
            <Divider
              mt="xl"
              mb="md"
              labelPosition="left"
              label={this.renderAppVersionWarningLabel(
                Localization.strings.AVATAR_INTERACTION,
                Localization.strings.AVATAR_INTERACTION_APP_VERSION_REQUIRED,
                true
              )}
            />
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {[
                { field: "moodEnabled", label: Localization.strings.MOOD_ENABLED, help: Localization.strings.MOOD_ENABLED_HELP },
                { field: "nudgeEnabled", label: Localization.strings.NUDGE_ENABLED, help: Localization.strings.NUDGE_ENABLED_HELP },
                { field: "chatTeacherToStudent", label: Localization.strings.CHAT_TEACHER_TO_STUDENT, help: Localization.strings.CHAT_TEACHER_TO_STUDENT_HELP },
                { field: "chatStudentToStudent", label: Localization.strings.CHAT_STUDENT_TO_STUDENT, help: Localization.strings.CHAT_STUDENT_TO_STUDENT_HELP },
                { field: "chatStudentToTeacher", label: Localization.strings.CHAT_STUDENT_TO_TEACHER, help: Localization.strings.CHAT_STUDENT_TO_TEACHER_HELP },
              ].map((opt) => (
                <div key={opt.field}>
                  <Group gap={6} align="center" wrap="nowrap" mb={4}>
                    <Text size="sm" fw={500} truncate>{opt.label}</Text>
                    <Tooltip label={opt.help} position="bottom-end" withArrow multiline w={300}>
                      <IconHelp size={18} style={{ color: 'var(--mantine-color-gray-5)' }} />
                    </Tooltip>
                  </Group>
                  <Select
                    value={String(this.state[opt.field])}
                    onChange={(v) => this.avatarInteractionChanged(opt.field, v)}
                    data={[
                      { value: "true", label: Localization.strings.YES },
                      { value: "false", label: Localization.strings.NO },
                    ]}
                    allowDeselect={false}
                    style={SETTING_FIELD_STYLE}
                  />
                </div>
              ))}
            </SimpleGrid>
          </Case>
        </Switch>

        <QRCodeDialog
          open={this.state.qrDialogOpen}
          onClose={() => this.setState({ qrDialogOpen: false })}
          localeId={this.props.root.getCurrentLocale()?.id}
          localeName={this.props.root.getCurrentLocale()?.name}
        />
      </div>
    )
  }
}

export default LocaleDetailView;
